import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
}

const steps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Moorly!',
    description: 'The social network for UK canal enthusiasts.',
  },
  {
    id: 'profile',
    title: 'Set up your profile',
    description: 'Tell others about yourself and your boating life.',
  },
  {
    id: 'interests',
    title: 'Select your interests',
    description: 'Follow topics that matter to you.',
  },
  {
    id: 'canals',
    title: 'Your home waters',
    description: 'Select the canals you frequent most.',
  },
];

const interests = [
  { id: 'narrowboat', label: 'Narrowboats', icon: '🚢' },
  { id: 'widebeam', label: 'Widebeams', icon: '🛥️' },
  { id: 'continuous_cruiser', label: 'Continuous Cruising', icon: '🗺️' },
  { id: 'marina', label: 'Marina Life', icon: '⚓' },
  { id: 'liveaboard', label: 'Liveaboard', icon: '🏠' },
  { id: 'day_boat', label: 'Day Boating', icon: '☀️' },
  { id: 'wildlife', label: 'Wildlife', icon: '🦆' },
  { id: 'photography', label: 'Photography', icon: '📷' },
  { id: 'engineering', label: 'Boat Maintenance', icon: '🔧' },
  { id: 'history', label: 'Canal History', icon: '📚' },
  { id: 'fishing', label: 'Fishing', icon: '🎣' },
  { id: 'cooking', label: 'Boat Cooking', icon: '🍳' },
];

const popularCanals = [
  { id: 'grand_union', name: 'Grand Union Canal' },
  { id: 'oxford', name: 'Oxford Canal' },
  { id: 'kennet_avon', name: 'Kennet & Avon Canal' },
  { id: 'leeds_liverpool', name: 'Leeds & Liverpool Canal' },
  { id: 'llangollen', name: 'Llangollen Canal' },
  { id: 'trent_mersey', name: 'Trent & Mersey Canal' },
  { id: 'shropshire_union', name: 'Shropshire Union Canal' },
  { id: 'staffordshire_worcestershire', name: 'Staffordshire & Worcestershire Canal' },
  { id: 'river_thames', name: 'River Thames' },
  { id: 'severn', name: 'River Severn' },
  { id: 'avon', name: 'River Avon' },
  { id: 'canal_du_midi', name: 'Other (UK)' },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [bio, setBio] = useState('');
  const [boatName, setBoatName] = useState('');
  const [boatType, setBoatType] = useState('');
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedCanals, setSelectedCanals] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSkip = () => {
    navigate('/');
  };
  
  const handleComplete = async () => {
    setSaving(true);
    try {
      await api.users.updateProfile({
        bio,
        boatName: boatName || undefined,
        boatType: boatType || undefined,
        interests: selectedInterests,
        homeCanals: selectedCanals,
        onboardingCompleted: true,
      });
      navigate('/');
    } catch (err) {
      console.error('Failed to save profile:', err);
    } finally {
      setSaving(false);
    }
  };
  
  const toggleInterest = (id: string) => {
    setSelectedInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };
  
  const toggleCanal = (id: string) => {
    setSelectedCanals((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };
  
  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case 'welcome':
        return (
          <div className="text-center py-8">
            <div className="text-6xl mb-6">⛵</div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              Welcome aboard, {user?.displayName}!
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
              Let's get you set up so you can start connecting with fellow boaters,
              sharing your journeys, and discovering the waterways.
            </p>
          </div>
        );
        
      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Bio
              </label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself and your boating adventures..."
                className="input min-h-[100px] resize-none"
                rows={4}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Boat Name (optional)
              </label>
              <input
                type="text"
                value={boatName}
                onChange={(e) => setBoatName(e.target.value)}
                placeholder="e.g., Wandering Star"
                className="input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Boat Type (optional)
              </label>
              <select
                value={boatType}
                onChange={(e) => setBoatType(e.target.value)}
                className="input"
              >
                <option value="">Select a type</option>
                <option value="narrowboat">Narrowboat</option>
                <option value="widebeam">Widebeam</option>
                <option value="cruiser">Cruiser</option>
                <option value="dutch_barge">Dutch Barge</option>
                <option value="day_boat">Day Boat</option>
                <option value="sailing">Sailing Boat</option>
                <option value="other">Other</option>
                <option value="none">I don't have a boat</option>
              </select>
            </div>
          </div>
        );
        
      case 'interests':
        return (
          <div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Select topics you're interested in (choose at least 3):
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {interests.map((interest) => (
                <button
                  key={interest.id}
                  onClick={() => toggleInterest(interest.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    selectedInterests.includes(interest.id)
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <span className="text-2xl">{interest.icon}</span>
                  <p className="mt-2 text-sm font-medium text-slate-900 dark:text-white">
                    {interest.label}
                  </p>
                </button>
              ))}
            </div>
            {selectedInterests.length > 0 && (
              <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                {selectedInterests.length} selected
              </p>
            )}
          </div>
        );
        
      case 'canals':
        return (
          <div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Select the canals you cruise most often:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {popularCanals.map((canal) => (
                <button
                  key={canal.id}
                  onClick={() => toggleCanal(canal.id)}
                  className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                    selectedCanals.includes(canal.id)
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <span className="text-lg">🌊</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {canal.name}
                  </span>
                  {selectedCanals.includes(canal.id) && (
                    <CheckIcon className="w-5 h-5 text-primary-500 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-slate-900 dark:to-slate-800 p-4 flex items-center justify-center">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className={`h-2 rounded-full transition-all ${
                idx === currentStep
                  ? 'w-8 bg-primary-500'
                  : idx < currentStep
                  ? 'w-4 bg-primary-300'
                  : 'w-4 bg-slate-200 dark:bg-slate-700'
              }`}
            />
          ))}
        </div>
        
        {/* Card */}
        <div className="card p-6 md:p-8">
          {/* Step header */}
          <div className="text-center mb-8">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">
              {steps[currentStep].title}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {steps[currentStep].description}
            </p>
          </div>
          
          {/* Content */}
          {renderStepContent()}
          
          {/* Actions */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100 dark:border-slate-700">
            <div>
              {currentStep > 0 ? (
                <button onClick={handleBack} className="btn btn-secondary">
                  Back
                </button>
              ) : (
                <button onClick={handleSkip} className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
                  Skip for now
                </button>
              )}
            </div>
            <button
              onClick={handleNext}
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : currentStep === steps.length - 1 ? 'Get Started' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
